import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BIDNavbar from "@/components/feature/BIDNavbar";
import BIDFooter from "@/components/feature/BIDFooter";
import { statusConfig, priorityConfig } from "@/mocks/ticketData";
import type { Ticket } from "@/mocks/ticketData";
import { fetchTicketDetail } from "@/services/zammadService";

export default function Tracking() {
  const navigate = useNavigate();
  const [ticketId, setTicketId] = useState("");
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const id = ticketId.trim();
    if (!id) return;

    setSearched(true);
    setLoading(true);
    setNotFound(false);
    setTicket(null);
    try {
      const found = await fetchTicketDetail(id);
      setTicket({
        id: found.id,
        title: found.title,
        category: found.category,
        subcategory: found.subcategory || "General",
        status: found.status,
        priority: found.priority,
        createdAt: found.createdAt,
        updatedAt: found.updatedAt,
        description: found.description || "Sin descripción adicional.",
        assignedTo: found.assignedTo || "Mesa de Ayuda",
        timeline: found.timeline || [],
      });
      setNotFound(false);
    } catch (err) {
      console.error(err);
      setTicket(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  const handleQuickSearch = useCallback(async (id: string) => {
    setTicketId(id);
    setSearched(true);
    setLoading(true);
    setNotFound(false);
    setTicket(null);
    try {
      const found = await fetchTicketDetail(id);
      setTicket({
        id: found.id,
        title: found.title,
        category: found.category,
        subcategory: found.subcategory || "General",
        status: found.status,
        priority: found.priority,
        createdAt: found.createdAt,
        updatedAt: found.updatedAt,
        description: found.description || "Sin descripción adicional.",
        assignedTo: found.assignedTo || "Mesa de Ayuda",
        timeline: found.timeline || [],
      });
      setNotFound(false);
    } catch (err) {
      console.error(err);
      setTicket(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const ticketDate = new Date(dateStr);
    if (isNaN(ticketDate.getTime())) return "N/A";

    const day = ticketDate.getDate();
    const month = ticketDate.getMonth() + 1;
    const year = ticketDate.getFullYear();
    const hours = String(ticketDate.getHours()).padStart(2, "0");
    const minutes = String(ticketDate.getMinutes()).padStart(2, "0");
    const months = [
      "Ene", "Feb", "Mar", "Abr", "May", "Jun",
      "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
    ];
    return `${day} ${months[month - 1]} ${year}, ${hours}:${minutes}`;
  };

  return (
    <main className="relative min-h-screen bg-background-50">
      <BIDNavbar />

      {/* Page Header */}
      <section className="w-full pt-24 md:pt-28 pb-8 md:pb-10 px-4 md:px-6 lg:px-10 bg-background-100/60">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <a
              href="/ti"
              className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              <span>Inicio</span>
            </a>
            <span className="text-foreground-300">/</span>
            <span className="text-sm text-foreground-700 font-medium">Seguimiento de Ticket</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
            Seguimiento de Ticket
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl">
            Ingresa el número de tu ticket para consultar su estado actual y el historial de atención.
          </p>
        </div>
      </section>

      {/* Search Area */}
      <section className="w-full py-8 md:py-10 px-4 md:px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-foreground-400 text-lg"></i>
              </div>
              <input
                type="text"
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Ej: TK-2026-0001"
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3.5 rounded-xl bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all flex items-center justify-center gap-2"
            >
              <i className="ri-search-line text-sm"></i>
              Buscar Ticket
            </button>
          </form>

          {/* Loading Indicator */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mb-3"></div>
              <p className="text-xs text-foreground-500 font-medium">Buscando ticket...</p>
            </div>
          )}

          {/* Not Found */}
          {searched && notFound && (
            <div className="rounded-2xl bg-background-50 border border-background-200 p-8 md:p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-accent-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                Ticket no encontrado
              </h3>
              <p className="text-sm text-foreground-500 max-w-md mx-auto mb-6">
                No encontramos ningún ticket con el ID <strong className="text-foreground-800">{ticketId}</strong>. Verifica el número e intenta de nuevo.
              </p>
              <button
                onClick={() => {
                  setTicketId("");
                  setSearched(false);
                  setNotFound(false);
                }}
                className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
              >
                Intentar de nuevo
              </button>
            </div>
          )}

          {/* Ticket Details */}
          {ticket && (
            <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
              {/* Ticket Header */}
              <div className="px-6 md:px-8 py-5 md:py-6 border-b border-background-200/70">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-foreground-500 bg-background-100 px-2.5 py-1 rounded-md">
                      {ticket.id}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig[ticket.status].bg} ${statusConfig[ticket.status].color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[ticket.status].color.replace("text-", "bg-")}`}></span>
                      {statusConfig[ticket.status].label}
                    </span>
                  </div>
                  <span className={`text-xs font-semibold ${priorityConfig[ticket.priority].color}`}>
                    Prioridad {priorityConfig[ticket.priority].label}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                  {ticket.title}
                </h2>
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-price-tag-3-line text-sm"></i>
                    {ticket.category} &gt; {ticket.subcategory}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line text-sm"></i>
                    {ticket.assignedTo}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-sm"></i>
                    Creado: {formatDate(ticket.createdAt)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-refresh-line text-sm"></i>
                    Actualizado: {formatDate(ticket.updatedAt)}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="px-6 md:px-8 py-5 border-b border-background-200/70">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  Descripción del Incidente
                </h4>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {ticket.description}
                </p>
              </div>

              {/* Timeline */}
              <div className="px-6 md:px-8 py-5">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                  Historial de Atención
                </h4>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[19px] top-2 bottom-2 w-px bg-background-200"></div>

                  <div className="space-y-0">
                    {ticket.timeline.map((event, idx) => (
                      <div key={idx} className="relative flex gap-4 pb-5 last:pb-0">
                        {/* Dot */}
                        <div className={`relative z-10 w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0 ${
                          idx === ticket.timeline.length - 1
                            ? "bg-primary-500"
                            : "bg-background-100 border-2 border-background-200"
                        }`}>
                          <i className={`${event.icon} ${
                            idx === ticket.timeline.length - 1 ? "text-white" : "text-foreground-400"
                          } text-base`}></i>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                            <h5 className="text-sm font-semibold text-foreground-900">
                              {event.title}
                            </h5>
                            <span className="text-xs text-foreground-400 whitespace-nowrap">
                              {formatDate(event.date)}
                            </span>
                          </div>
                          <p className="text-sm text-foreground-600">
                            {event.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA if no ticket searched */}
      {!ticket && !notFound && !searched && (
        <section className="w-full pb-16 md:pb-20 px-4 md:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl bg-background-100/70 border border-background-200 p-6 md:p-8 text-center">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                ¿No tienes un ticket todavía?
              </h3>
              <p className="text-sm text-foreground-500 max-w-md mx-auto mb-5">
                Si aún no has reportado tu incidente, puedes crear uno nuevo desde el portal principal.
              </p>
              <a
                href="/ti"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
              >
                <i className="ri-add-line text-sm"></i>
                Crear Nuevo Ticket
              </a>
            </div>
          </div>
        </section>
      )}

      <BIDFooter />
    </main>
  );
}
